/**
 * Settings → Scores and flags (step 6; ADR-001 §8.1–§8.7, §11.8; operator decision 11: founder and associate
 * director edit, every change audited). Guide §6: "These are the exact numbers in the prototype. Keep them
 * configurable." The prototype has no Settings screen for them, so this tab follows its Settings idiom: panels,
 * `.fld` inputs, the `tgl()` switch, and the "Rules watching" list from VIEWS.flags (L4353).
 *
 * Data: GET + PATCH /settings/thresholds and GET + PATCH /settings/flags. Both PATCHes send the shared
 * config_version as If-Match; a 412 means someone else saved first: the page reloads their numbers, marks any box
 * both of you changed, and keeps edits to the boxes they did not touch.
 */
import { useState, type ReactNode } from 'react';
import type { FlagState, PatchThresholdsBody, Thresholds as T } from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { dayMonth, useUpdateWorkingDay, useWorkingDaySetting } from '../../lib/timesheets';
import { useFlags, useThresholds, useUpdateFlags, useUpdateThresholds } from '../../lib/health';
import { Empty, ErrorState, Loading, Switch, errorText, useToast } from '../../ui/kit';

const STALE = 'Someone else changed Settings a moment ago. The page now shows their change. Make yours again.';

// ---------------------------------------------------------------- value paths

type Path = string; // "health.band_watch", "tone.penalties.leave"
const get = (t: T, p: Path): unknown => p.split('.').reduce<unknown>((o, k) => (o as Record<string, unknown>)?.[k], t);

/** Only the values that differ from what the server has, nested the way PATCH /settings/thresholds takes them. */
function changedBody(t: T, draft: Record<Path, unknown>): PatchThresholdsBody | null {
  const body: Record<string, Record<string, unknown>> = {};
  let n = 0;
  for (const [p, v] of Object.entries(draft)) {
    if (v === get(t, p)) continue;
    const [g, k, k2] = p.split('.') as [string, string, string?];
    body[g] ??= {};
    if (k2) body[g][k] = { ...((body[g][k] as Record<string, unknown>) ?? {}), [k2]: v };
    else body[g][k] = v;
    n++;
  }
  return n ? (body as PatchThresholdsBody) : null;
}

// ---------------------------------------------------------------- the health rules

/** One rule line: the words, an optional line value with its unit, and the points it takes off (or adds). */
type Rule = { label: string; line?: Path; unit?: string; pts?: Path; cap?: Path; plus?: boolean };
const BANDS: Rule[] = [
  { label: 'Healthy from a score of', line: 'health.band_healthy' },
  { label: 'Watch from a score of', line: 'health.band_watch' },
  { label: 'The score never goes below', line: 'health.floor' },
];
const RULES: Rule[] = [
  { label: 'Utilisation below', line: 'health.util_low', unit: '% of what they pay for', pts: 'health.pen_util_low' },
  { label: 'Utilisation below', line: 'health.util_watch', unit: '%', pts: 'health.pen_util_watch' },
  { label: 'Usage below their own baseline by more than', line: 'health.trend_fall', unit: '%', pts: 'health.pen_trend_fall' },
  { label: 'Usage below their own baseline by more than', line: 'health.trend_dip', unit: '%', pts: 'health.pen_trend_dip' },
  { label: 'Each overdue task', pts: 'health.pen_overdue_each', cap: 'health.overdue_cap' },
  { label: 'Each open escalation', pts: 'health.pen_escalation_each' },
  { label: 'Each request past its response time', pts: 'health.pen_request_breach_each' },
  { label: 'Nothing from the client for more than', line: 'health.quiet_days_soft', unit: 'days', pts: 'health.pen_quiet_soft' },
  { label: 'Nothing from the client for more than', line: 'health.quiet_days_hard', unit: 'days', pts: 'health.pen_quiet_hard' },
  { label: 'Each client email waiting for our reply', line: 'health.needs_min_days', unit: 'days or more', pts: 'health.pen_needs_each', cap: 'health.needs_cap' },
  { label: 'Our email unanswered for', line: 'health.noreply_days', unit: 'days or more', pts: 'health.pen_noreply' },
  { label: 'Unhappy emails in the last', line: 'health.tone_window_days', unit: 'days, the two worst', cap: 'health.tone_cap' },
  { label: 'A product with no usage data', pts: 'health.pen_no_data' },
  { label: 'A positive email from the client', pts: 'health.bonus_positive', plus: true },
];

/** The smaller groups, as plain fields. */
type FieldSpec = { path: Path; label: string; step?: number; kind?: 'number' | 'time' | 'countdown' | 'switch' };
const GROUPS: { title: string; sub: string; fields: FieldSpec[] }[] = [
  {
    title: 'Email tone',
    sub: 'Points off the health score for each kind of email. A positive email adds points, so it is below zero here.',
    fields: [
      { path: 'tone.enabled', label: 'Read the tone of client emails', kind: 'switch' },
      { path: 'tone.penalties.leave', label: 'Hints at leaving' },
      { path: 'tone.penalties.escalate', label: 'Threatens escalation' },
      { path: 'tone.penalties.negative', label: 'Sounds unhappy' },
      { path: 'tone.penalties.urgent', label: 'Urgent' },
      { path: 'tone.penalties.neutral', label: 'Neutral' },
      { path: 'tone.penalties.positive', label: 'Positive' },
    ],
  },
  {
    title: 'Engagement',
    sub: 'A cell on the engagement grid turns amber this many days before it is due.',
    fields: [{ path: 'engagement.due_soon_days', label: 'Due soon (days)' }],
  },
  {
    title: 'Usage forecast',
    sub: 'How the month-end usage line is drawn. A noisy fit is hidden rather than drawn.',
    fields: [
      { path: 'forecast.window_days', label: 'Look back (days)' },
      { path: 'forecast.horizon_days', label: 'Look ahead (days)' },
      { path: 'forecast.damping', label: 'Damping (0.1 to 1)', step: 0.05 },
      { path: 'forecast.util_cap', label: 'Highest utilisation drawn (%)' },
      { path: 'forecast.min_r2', label: 'Least fit to draw a line (0 to 1)', step: 0.05 },
    ],
  },
  {
    title: 'Timesheets',
    sub: 'The time a timesheet counts as on time.',
    // The hours of a working day are their own setting (GET/PATCH /settings/working-day — the value every
    // expected-hours number uses), edited in the "Working day" panel above, not as a threshold.
    fields: [
      { path: 'timesheet.on_time_by', label: 'On time if filed by', kind: 'time' },
    ],
  },
  // Voice and approval (capture.*) has its own tab, as in the prototype: Settings → Voice and approval.
  {
    title: 'Work',
    sub: 'When a blocked task is flagged, and how long a client request has for a response.',
    fields: [
      { path: 'work.block_flag_days', label: 'Flag a task blocked for more than (days)' },
      { path: 'work.client_response_hours', label: 'Client request response time (hours)' },
    ],
  },
];

// ---------------------------------------------------------------- screen

export function Thresholds() {
  return (
    <div className="cols c21" style={{ alignItems: 'start', gap: 22 }}>
      <div style={{ display: 'grid', gap: 22 }}>
        <div className="panel">
          <div className="hd">
            <h3>Working day</h3>
          </div>
          <div className="bd">
            <div className="sub" style={{ fontSize: 12.5, marginBottom: 12 }}>
              The hours of a full working day. Half days count as half. Every expected-hours number uses it.
            </div>
            <WorkingDayField />
          </div>
        </div>
        <ThresholdsForm />
      </div>
      <RulesWatching />
    </div>
  );
}

function ThresholdsForm() {
  const q = useThresholds();
  const save = useUpdateThresholds();
  const toast = useToast();
  const [draft, setDraft] = useState<Record<Path, unknown>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});

  if (q.isPending) return <Loading label="Loading the thresholds…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { settings: t, defaults: d, config_version, updated_at, updated_by } = q.data;
  const val = (p: Path) => (p in draft ? draft[p] : get(t, p));
  const set = (p: Path, v: unknown) => {
    setDraft((x) => ({ ...x, [p]: v }));
    setErrs((x) => {
      const { [p]: _, ...rest } = x;
      return rest;
    });
  };
  const body = changedBody(t, draft);
  const bad = Object.entries(draft).some(([, v]) => typeof v === 'number' && Number.isNaN(v));

  const num = (p: Path, w = 64, step = 1) => (
    <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
      <input
        className="in mins"
        type="number"
        step={step}
        style={{ width: w, padding: '6px 8px' }}
        aria-label={p}
        value={Number.isNaN(val(p)) ? '' : String(val(p) ?? '')}
        onChange={(e) => set(p, e.target.value === '' ? Number.NaN : Number(e.target.value))}
        title={`Default ${String(get(d, p))}`}
      />
      {errs[p] ? <small className="ferr">{errs[p]}</small> : null}
    </span>
  );

  const submit = () => {
    if (bad) return toast('Enter a number in every box first');
    if (!body) return;
    save.mutate(
      { version: config_version, body },
      {
        onSuccess: (r) => {
          setDraft({});
          setErrs({});
          toast(`Saved. Scores use these numbers from now on (version ${r.config_version}). Past scores keep theirs.`);
        },
        onError: async (e) => {
          if (e instanceof ApiFailure && e.status === 412) {
            // Someone else saved first. Each box you edited that they also changed now shows THEIR number, marked
            // with what you had typed; your edits to boxes they did not touch are kept for one more Save.
            const before = t;
            const fresh = (await q.refetch()).data?.settings;
            if (!fresh) return toast(STALE);
            const clash = (Object.keys(draft) as Path[]).filter((p) => get(fresh, p) !== get(before, p));
            if (!clash.length) return toast('Someone else changed other settings just now. Your numbers are kept: press Save again to apply them.');
            setDraft((x) => {
              const next = { ...x };
              for (const p of clash) delete next[p];
              return next;
            });
            setErrs(Object.fromEntries(clash.map((p) => [p, `Changed by someone else to ${String(get(fresh, p))}. You had typed ${String(draft[p])}.`])));
            toast(
              `Someone else changed ${clash.length === 1 ? 'a box you edited' : `${clash.length} boxes you edited`} just now. ${clash.length === 1 ? 'It shows' : 'They show'} their number, marked in red; your other edits are kept. Check and Save again.`,
            );
          } else if (e instanceof ApiFailure && Object.keys(e.fieldErrors).length) {
            setErrs(e.fieldErrors);
            toast('Some numbers do not fit together. See the boxes marked in red.');
          } else toast(errorText(e));
        },
      },
    );
  };

  const ruleRow = (r: Rule, i: number) => (
    <tr key={i}>
      <td>
        <span className="split" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span>{r.label}</span>
          {r.line ? num(r.line) : null}
          {r.unit ? <span className="sub">{r.unit}</span> : null}
        </span>
      </td>
      <td className="tr" style={{ whiteSpace: 'nowrap' }}>
        {r.pts ? (
          <span className="split" style={{ gap: 6, justifyContent: 'flex-end' }}>
            <span className="sub">{r.plus ? '+' : '−'}</span>
            {num(r.pts, 56)}
          </span>
        ) : null}
      </td>
      <td className="tr" style={{ whiteSpace: 'nowrap' }}>
        {r.cap ? (
          <span className="split" style={{ gap: 6, justifyContent: 'flex-end' }}>
            <span className="sub">at most −</span>
            {num(r.cap, 56)}
          </span>
        ) : (
          <span className="sub">-</span>
        )}
      </td>
    </tr>
  );

  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <div className="panel">
        <div className="hd">
          <h3>Health score</h3>
          <div className="r">Starts at 100. Each line takes points off, or adds them</div>
        </div>
        <div className="bd" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', borderBottom: '1px solid var(--line)' }}>
          {BANDS.map((b) => (
            <span key={b.line} className="split" style={{ gap: 8 }}>
              <span>{b.label}</span>
              {num(b.line!)}
            </span>
          ))}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th className="tr">Points</th>
                <th className="tr">Cap</th>
              </tr>
            </thead>
            <tbody>{RULES.map(ruleRow)}</tbody>
          </table>
        </div>
      </div>

      {GROUPS.map((g) => (
        <div className="panel" key={g.title}>
          <div className="hd">
            <h3>{g.title}</h3>
          </div>
          <div className="bd">
            <div className="sub" style={{ fontSize: 12.5, marginBottom: 12 }}>
              {g.sub}
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {g.fields.map((f) => (
                <label className="fld" key={f.path} style={{ margin: 0 }}>
                  <span>{f.label}</span>
                  {f.kind === 'switch' ? (
                    <Switch on={val(f.path) === true} onClick={() => set(f.path, !(val(f.path) === true))} label={f.label} />
                  ) : f.kind === 'time' ? (
                    <input className="in" type="time" style={{ maxWidth: 140 }} value={String(val(f.path))} onChange={(e) => set(f.path, e.target.value)} />
                  ) : f.kind === 'countdown' ? (
                    <select className="in" style={{ maxWidth: 160 }} value={String(val(f.path))} onChange={(e) => set(f.path, Number(e.target.value))}>
                      <option value="60">1 minute</option>
                      <option value="120">2 minutes</option>
                      <option value="180">3 minutes</option>
                    </select>
                  ) : (
                    num(f.path, 110, f.step)
                  )}
                  {errs[f.path] && f.kind ? <small className="ferr">{errs[f.path]}</small> : null}
                </label>
              ))}
            </div>
          </div>
        </div>
      ))}

      <div className="split" style={{ gap: 10, flexWrap: 'wrap' }}>
        <span className="sub" style={{ fontSize: 12.5 }}>
          {updated_by && updated_at ? `Last changed by ${updated_by.name} on ${dayMonth(updated_at.slice(0, 10))}. ` : 'These are the starting numbers. '}
          Every change is recorded. Past health scores keep the numbers they were worked out with.
        </span>
        <div className="grow" />
        {body || Object.keys(draft).length ? (
          <button className="btn" onClick={() => (setDraft({}), setErrs({}))} disabled={save.isPending}>
            Discard
          </button>
        ) : null}
        <button className="btn pri" onClick={submit} disabled={!body || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- the twelve flags

const TIER: Record<FlagState['tier'], string> = {
  event: 'As it happens',
  nightly: 'Every night',
  every_15_min: 'Every 15 minutes',
  every_5_min: 'Every 5 minutes',
};

/** Prototype VIEWS.flags "Rules watching" panel: one switch per rule, with how many flags are open now. */
/**
 * `rail`: the Money → Risk flags side panel (prototype VIEWS.flags): a checkbox and the rule's one-line label. Settings
 * keeps the switch with its schedule and the open count. Switching off a rule with open flags asks first in both.
 */
export function RulesWatching({ rail }: { rail?: boolean } = {}) {
  const q = useFlags();
  const save = useUpdateFlags();
  const toast = useToast();
  const [confirmOff, setConfirmOff] = useState<FlagState | null>(null);

  if (q.isPending) return <Loading label="Loading the rules…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { flags, config_version } = q.data;
  const on = flags.filter((f) => f.enabled).length;

  const flip = (f: FlagState) =>
    save.mutate(
      { version: config_version, body: { [f.key]: !f.enabled } },
      {
        onSuccess: () => {
          setConfirmOff(null);
          toast(f.enabled ? `Switched off${f.open_hits ? `. Its ${f.open_hits} open flag${f.open_hits === 1 ? ' was' : 's were'} cleared` : ''}.` : 'Switched on. It starts checking at its next run.');
        },
        onError: (e) => {
          if (e instanceof ApiFailure && e.status === 412) {
            toast(STALE);
            void q.refetch();
          } else toast(errorText(e));
        },
      },
    );

  return (
    <div className="panel" style={{ alignSelf: 'start' }}>
      <div className="hd">
        <h3>Rules watching</h3>
        <div className="r">{on} on</div>
      </div>
      {flags.length ? (
        flags.map((f) => (
          <div key={f.key} className="li" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {rail ? (
              <label className="split" style={{ cursor: 'pointer', gap: 10, flex: 1, minWidth: 0 }}>
                <input
                  type="checkbox"
                  checked={f.enabled}
                  disabled={save.isPending}
                  onChange={() => (f.enabled && f.open_hits ? setConfirmOff(f) : flip(f))}
                />
                <div className="b" style={{ fontSize: 13 }}>
                  {f.label}
                </div>
              </label>
            ) : (
              <>
                <Switch
                  on={f.enabled}
                  label={f.label}
                  disabled={save.isPending}
                  onClick={() => (f.enabled && f.open_hits ? setConfirmOff(f) : flip(f))}
                />
                <div className="b" style={{ fontSize: 13 }}>
                  <div>{f.label}</div>
                  <div className="m">
                    <span>{TIER[f.tier]}</span>
                    {f.manager_only ? <span>Managers only</span> : null}
                  </div>
                </div>
                {f.open_hits ? <span className="chip warm">{f.open_hits} open</span> : null}
              </>
            )}
            {confirmOff?.key === f.key ? (
              <Confirm>
                <span className="sub">This clears its {f.open_hits} open flag{f.open_hits === 1 ? '' : 's'}.</span>
                <button className="btn sm bad" disabled={save.isPending} onClick={() => flip(f)}>
                  Switch off
                </button>
                <button className="btn sm ghost" onClick={() => setConfirmOff(null)}>
                  Keep on
                </button>
              </Confirm>
            ) : null}
          </div>
        ))
      ) : (
        <Empty title="No rules yet" />
      )}
    </div>
  );
}

function Confirm({ children }: { children: ReactNode }) {
  return (
    <div className="split" style={{ gap: 8, width: '100%', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 6 }}>
      {children}
    </div>
  );
}

/** "Hours in a working day" — saved on its own (If-Match config_version); half days are half of it. */
function WorkingDayField() {
  const q = useWorkingDaySetting();
  const save = useUpdateWorkingDay();
  const toast = useToast();
  const [v, setV] = useState<string | null>(null);
  if (q.isPending) return <span className="sub">Loading the working day…</span>;
  if (q.isError) return <span className="ferr">{errorText(q.error)}</span>;
  const cur = q.data.working_day_hours;
  const val = v ?? String(cur);
  const n = Number(val);
  const ok = val.trim() !== '' && n >= 1 && n <= 12 && Math.round(n * 4) === n * 4;
  const dirty = ok && n !== cur;
  return (
    <label className="fld" style={{ margin: 0 }}>
      <span>Hours in a working day</span>
      <span className="split" style={{ gap: 8 }}>
        <input className="in" type="number" min={1} max={12} step={0.25} style={{ maxWidth: 110 }} value={val} onChange={(e) => setV(e.target.value)} aria-invalid={!ok || undefined} />
        {dirty ? (
          <button
            type="button"
            className="btn sm pri"
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                { version: q.data.config_version, hours: n },
                {
                  onSuccess: (r) => {
                    setV(null);
                    toast(`A working day is now ${r.working_day_hours} h. Expected hours use it from now on.`);
                  },
                  onError: (e) => {
                    if (e instanceof ApiFailure && e.status === 412) {
                      // A genuine concurrent change: fetch the new version but keep what was typed, so one more
                      // Save applies it.
                      toast('Someone else changed the settings just now. Your value is kept: press Save again to apply it.');
                      void q.refetch();
                    } else toast(errorText(e));
                  },
                },
              )
            }
          >
            Save
          </button>
        ) : null}
      </span>
      {!ok ? <small className="ferr">Use quarter hours between 1 and 12, for example 7.5</small> : null}
    </label>
  );
}
